import { useState, useEffect } from 'react';

/**
 * Hook to calculate dynamic background height that fits content and aspect ratio
 */
export function useDynamicBackground() {
  const [backgroundHeight, setBackgroundHeight] = useState('100vh');

  useEffect(() => {
    const updateBackgroundHeight = () => {
      // Get the full document height
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );

      // Get viewport height
      const viewportHeight = window.innerHeight;

      // Use the larger of the two
      const finalHeight = Math.max(documentHeight, viewportHeight);

      setBackgroundHeight(`${finalHeight}px`);
    };

    // Update on mount
    updateBackgroundHeight();

    // Update on window resize
    window.addEventListener('resize', updateBackgroundHeight);

    // Update on content changes (using MutationObserver)
    const observer = new MutationObserver(updateBackgroundHeight);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });

    // Also update on scroll (for dynamic content loading)
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateBackgroundHeight();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', updateBackgroundHeight);
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return backgroundHeight;
}
